/**
 * TEST-SERVER SAVE MODE (dev tool).
 *
 * On the STAGING/test server only, an admin can choose which save the game loads:
 *   • "test" (default) — the normal, independent test save. Reads AND writes the test DB; persists
 *     across reopens exactly like a real save.
 *   • "live-readonly" — a READ-ONLY snapshot of the admin's LIVE save, loaded into the test game so you
 *     can look at / play around with your real progress on test. Autosave is fully DISABLED in this
 *     mode: nothing is written to live (it stays untouched) OR to the test save (it's left intact). Exit
 *     the mode → your test save is exactly as you left it.
 *
 * The choice lives in localStorage so a reload during a session stays in the same mode. It only has any
 * effect on the test server; on live this always reports "test" (normal saving).
 */
import { cachedEnv } from "./env";

export type SaveMode = "test" | "live-readonly";

const KEY = "shelf-garden.saveMode";

/** The current save mode (from localStorage), defaulting to "test". */
export function getSaveMode(): SaveMode {
  try { return localStorage.getItem(KEY) === "live-readonly" ? "live-readonly" : "test"; } catch { return "test"; }
}

/** Persist the chosen save mode. */
export function setSaveMode(mode: SaveMode): void {
  try { localStorage.setItem(KEY, mode); } catch { /* storage blocked — mode won't persist, fine */ }
}

/**
 * True when the game is showing the LIVE save read-only (⇒ saving must be a no-op). SAFETY: this can
 * only ever be true on the STAGING server — we AND it with the cached env, so a stale localStorage flag
 * carried to the LIVE site (same key) can never disable live saving. On live, `cachedEnv()` is
 * "production" (or null before config loads, which also returns false), so live always saves normally.
 */
export function isLiveReadonly(): boolean {
  return cachedEnv() === "staging" && getSaveMode() === "live-readonly";
}
